<template>
  <div class="flow-overview-card" :class="{ 'flow-overview-card--embedded': embedded }">
    <h3>流程概覽</h3>
    <p class="flow-overview-intro">
      共 {{ items.length }} 個步驟。以下為整體行銷活動流程的觸發事件與動作摘要（點擊步驟可跳至編輯）。
    </p>

    <div class="flow-overview-metrics">
      <div class="flow-metric">
        <span class="flow-metric-label">預估受眾</span>
        <div class="flow-metric-value">
          <template v-if="estimateLoading">
            <span class="flow-metric-loading">計算中...</span>
          </template>
          <template v-else-if="estimateError">
            <span class="flow-metric-error">—</span>
          </template>
          <template v-else-if="estimatedAudience != null">
            <strong>{{ formatCount(estimatedAudience) }}</strong>
            <span class="flow-metric-unit">人</span>
          </template>
          <template v-else>
            <span class="flow-metric-empty">—</span>
          </template>
        </div>
        <p v-if="!estimateLoading && !estimateError && audienceUnrestricted" class="flow-metric-hint">
          未設定額外參與限制
        </p>
      </div>

      <div class="flow-metric">
        <span class="flow-metric-label">預估成本</span>
        <div class="flow-metric-value">
          <template v-if="estimateLoading">
            <span class="flow-metric-loading">計算中...</span>
          </template>
          <template v-else-if="estimateError">
            <span class="flow-metric-error">—</span>
          </template>
          <template v-else-if="totalCost != null">
            <strong>{{ formatMoney(totalCost) }}</strong>
          </template>
          <template v-else>
            <span class="flow-metric-empty">—</span>
          </template>
        </div>
        <p v-if="!estimateLoading && !estimateError && costBreakdown" class="flow-metric-hint">
          推播 {{ formatPushCost(costBreakdown.lineMessageCost) }} ·
          點數 {{ formatMoney(costBreakdown.pointsCost) }} ·
          券 {{ formatMoney(costBreakdown.couponCost) }}
        </p>
      </div>
    </div>

    <p v-if="estimateError" class="flow-estimate-error">
      <i class="ri-error-warning-line"></i>
      {{ estimateError }}
    </p>
    <p v-else class="flow-estimate-note">
      券成本依模板「單券預估成本」× 受眾人數計算（假設全數核銷）；推播 0.2 元／則。以上為預估值，實際依執行結果為準。
    </p>

    <ol class="flow-overview-list">
      <li
        v-for="item in items"
        :key="item.stepKey"
        class="flow-overview-item"
        :class="{ active: item.stepKey === selectedStepKey }"
        role="button"
        tabindex="0"
        @click="$emit('select', item.stepKey)"
        @keydown.enter="$emit('select', item.stepKey)"
      >
        <div class="flow-step-head">
          <span class="flow-step-order">{{ item.stepOrder }}</span>
          <strong class="flow-step-name">{{ item.stepName }}</strong>
        </div>

        <div class="flow-step-row">
          <span class="flow-label">觸發</span>
          <span class="flow-value">{{ item.trigger }}</span>
        </div>

        <div v-if="item.condition" class="flow-step-row">
          <span class="flow-label">條件</span>
          <span class="flow-value">{{ item.condition }}</span>
        </div>

        <div class="flow-step-row">
          <span class="flow-label">動作</span>
          <div class="flow-value">
            <ul v-if="item.actions.length" class="flow-action-list">
              <li v-for="(action, idx) in item.actions" :key="idx">{{ action }}</li>
            </ul>
            <span v-else class="flow-empty">（無動作，純等待）</span>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>

<script>
import { computed } from 'vue';
import { buildFlowOverview, formatPushCost } from '@/constants/marketingCampaign.js';

export default {
  name: 'MarketingCampaignFlowOverview',
  props: {
    steps: { type: Array, default: () => [] },
    couponTemplates: { type: Array, default: () => [] },
    lineTemplates: { type: Array, default: () => [] },
    lotteryConfigs: { type: Array, default: () => [] },
    wixtarTickets: { type: Array, default: () => [] },
    selectedStepKey: { type: String, default: null },
    embedded: { type: Boolean, default: false },
    estimatedAudience: { type: Number, default: null },
    totalCost: { type: Number, default: null },
    costBreakdown: { type: Object, default: null },
    audienceUnrestricted: { type: Boolean, default: false },
    estimateLoading: { type: Boolean, default: false },
    estimateError: { type: String, default: null }
  },
  emits: ['select'],
  setup(props) {
    const items = computed(() =>
      buildFlowOverview(props.steps, {
        couponTemplates: props.couponTemplates,
        lineTemplates: props.lineTemplates,
        lotteryConfigs: props.lotteryConfigs,
        wixtarTickets: props.wixtarTickets
      })
    );

    const formatCount = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      return num.toLocaleString('zh-TW');
    };

    const formatMoney = (value) => {
      const num = Number(value);
      if (!Number.isFinite(num)) return '—';
      return `$${num.toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`;
    };

    return { items, formatCount, formatMoney, formatPushCost };
  }
};
</script>

<style scoped>
.flow-overview-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
}

.flow-overview-card--embedded {
  margin-bottom: 0;
}

.flow-overview-card--embedded .flow-overview-intro {
  font-size: 12px;
}

.flow-overview-card--embedded .flow-step-row {
  grid-template-columns: 44px 1fr;
  font-size: 12px;
}

.flow-overview-card--embedded .flow-step-name {
  font-size: 14px;
}

.flow-overview-card h3 {
  margin: 0 0 8px;
  font-size: 16px;
}

.flow-overview-intro {
  margin: 0 0 12px;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}

.flow-overview-metrics {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

.flow-metric {
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  min-width: 0;
}

.flow-metric-label {
  display: block;
  font-size: 12px;
  color: #475569;
  margin-bottom: 6px;
  font-weight: 600;
}

.flow-metric-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-height: 28px;
}

.flow-metric-value strong {
  font-size: 22px;
  line-height: 1.1;
  color: #111827;
}

.flow-overview-card--embedded .flow-metric-value strong {
  font-size: 20px;
}

.flow-metric-unit {
  font-size: 13px;
  color: #64748b;
}

.flow-metric-loading,
.flow-metric-empty,
.flow-metric-error {
  font-size: 14px;
  color: #9ca3af;
}

.flow-metric-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
}

.flow-estimate-note,
.flow-estimate-error {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #475569;
}

.flow-estimate-error {
  color: #b45309;
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.flow-estimate-error i {
  margin-top: 1px;
}

.flow-overview-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.flow-overview-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 14px;
  background: #fafbfc;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.flow-overview-item:hover {
  border-color: #b6d4fe;
  background: #f8fbff;
}

.flow-overview-item.active {
  border-color: #0d6efd;
  background: #eef4ff;
}

.flow-step-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.flow-step-order {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0d6efd;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
}

.flow-step-name {
  font-size: 15px;
}

.flow-step-row {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 8px;
  align-items: start;
  font-size: 13px;
  margin-top: 6px;
}

.flow-label {
  color: #475569;
  font-weight: 600;
}

.flow-value {
  color: #212529;
}

.flow-action-list {
  margin: 0;
  padding-left: 18px;
}

.flow-action-list li + li {
  margin-top: 4px;
}

.flow-empty {
  color: #64748b;
}
</style>
